/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
} from "@arcadeai/design-system/rhf";
import { ChevronDownIcon, InfoIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

const ALLOWED_RESPONSE_MAP_KEYS = [
  { key: "access_token", placeholder: "$.access_token" },
  { key: "expires_in", placeholder: "$.expires_in" },
  { key: "refresh_token", placeholder: "$.refresh_token" },
  { key: "scope", placeholder: "$.scope" },
  { key: "token_type", placeholder: "$.token_type" },
] as const;

type ResponseMapField = {
  key: string;
  placeholder: string;
  enabled: boolean;
  value: string;
};

type EnforcedResponseMapInputProps = {
  value?: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  // biome-ignore lint/suspicious/noExplicitAny: form can have various types
  form?: UseFormReturn<any>;
  name?: string;
};

export const EnforcedResponseMapInput = ({
  value = {},
  onChange,
}: EnforcedResponseMapInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [responseMapFields, setResponseMapFields] = useState<
    ResponseMapField[]
  >(() =>
    ALLOWED_RESPONSE_MAP_KEYS.map(({ key, placeholder }) => ({
      key,
      placeholder,
      enabled: key in value,
      value: value[key] || placeholder,
    }))
  );

  // Update fields when value prop changes
  useEffect(() => {
    const updatedFields = ALLOWED_RESPONSE_MAP_KEYS.map(
      ({ key, placeholder }) => ({
        key,
        placeholder,
        enabled: key in value,
        value: value[key] || placeholder,
      })
    );

    setResponseMapFields(updatedFields);

    // Auto-open if there are enabled fields
    const hasEnabledFields = updatedFields.some((field) => field.enabled);
    if (hasEnabledFields) {
      setIsOpen(true);
    }
  }, [value]);

  // Helper function to update onChange with Record format.
  // Emits an empty object (not null) when no entries are enabled so the
  // backend can distinguish "explicitly cleared" from "field absent (preserve)".
  const updateRecord = useCallback(
    (updatedFields: ResponseMapField[]) => {
      const record = updatedFields
        .filter((field) => field.enabled && field.value.trim())
        .reduce(
          (acc, field) => {
            acc[field.key] = field.value;
            return acc;
          },
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          {} as Record<string, string>
        );
      onChange(record);
    },
    [onChange]
  );

  const handleToggleField = useCallback(
    (key: string, enabled: boolean) => {
      const updatedFields = responseMapFields.map((field) =>
        field.key === key
          ? {
              ...field,
              enabled,
              value: enabled ? field.placeholder : field.value,
            }
          : field
      );
      setResponseMapFields(updatedFields);
      updateRecord(updatedFields);
    },
    [responseMapFields, updateRecord]
  );

  const handleValueChange = useCallback(
    (key: string, newValue: string) => {
      const updatedFields = responseMapFields.map((field) =>
        field.key === key ? { ...field, value: newValue } : field
      );
      setResponseMapFields(updatedFields);
      updateRecord(updatedFields);
    },
    [responseMapFields, updateRecord]
  );

  const enabledCount = responseMapFields.filter(
    (field) => field.enabled
  ).length;

  return (
    <TooltipProvider>
      <Collapsible onOpenChange={setIsOpen} open={isOpen}>
        <div className="rounded-lg border border-zinc-600">
          <CollapsibleTrigger className="group flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <FormLabel className="font-medium text-sm">
                Response Map
              </FormLabel>
              <Tooltip>
                <TooltipTrigger type="button">
                  <InfoIcon className="size-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="w-auto max-w-md" side="top">
                  <div className="space-y-2 text-sm">
                    <p>
                      Configure how your custom OAuth provider's response fields
                      map to standard token fields. If your provider returns
                      tokens with different field names or nested structures,
                      use JSONPath expressions like <code>$.token</code> to
                      extract values.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Learn more about{" "}
                      <a
                        className="text-link hover:underline"
                        href="https://docs.arcade.dev/home/auth-providers/oauth2#jsonpath-expressions-in-response_map"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        JSONPath expressions in response maps
                      </a>
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
              {enabledCount > 0 && (
                <span className="rounded-full bg-primary px-2 py-1 text-primary-foreground text-xs">
                  {enabledCount} enabled
                </span>
              )}
            </div>
            <ChevronDownIcon className="size-4 text-muted-foreground transition-transform duration-200 group-data-[panel-open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            <div className="space-y-3">
              <FormDescription>
                {`Enable and configure the token fields your OAuth provider returns.${enabledCount === 0 ? " Leave all disabled if your provider uses standard field names." : ""}`}
              </FormDescription>
              <div className="space-y-3">
                {responseMapFields.map((field) => (
                  <div
                    className="flex items-center gap-3 rounded-md border p-3"
                    key={field.key}
                  >
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={field.enabled}
                        onCheckedChange={(enabled) =>
                          handleToggleField(field.key, enabled)
                        }
                      />
                    </div>
                    <div className="grid flex-1 grid-cols-2 gap-2">
                      <FormItem>
                        <FormControl>
                          <Input
                            className="bg-muted/50 text-muted-foreground"
                            disabled
                            value={field.key}
                          />
                        </FormControl>
                      </FormItem>
                      <FormItem>
                        <FormControl>
                          <Input
                            className={
                              field.enabled
                                ? ""
                                : "bg-muted/50 text-muted-foreground"
                            }
                            disabled={!field.enabled}
                            onChange={(e) =>
                              handleValueChange(field.key, e.target.value)
                            }
                            placeholder={field.placeholder}
                            value={field.value}
                          />
                        </FormControl>
                      </FormItem>
                    </div>
                  </div>
                ))}
              </div>
              {enabledCount === 0 && (
                <p className="text-muted-foreground text-sm">
                  No custom mappings configured.
                </p>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </TooltipProvider>
  );
};
